import React, {useState, useMemo} from 'react';
import ModalDialog from '../../../../modules/ModalDialog/ModalDialog';
import {ParsedLearner} from '../../../../utils/bulkImportParser';
import Checkbox from '../../../../uiToolkit/Inputs/Checkbox';
import Button from '../../../../uiToolkit/Buttons/Button';
import SearchDropdown from '../../../TapyblAI/GenerateAIAssistant/components/SearchDropdown';
import {learnersText} from '../../learnersText';
import DatePickerComponent from '../../../CourseDetails/CourseLearners/components/DatePickerComponent';
import TextInput2 from '../../../../uiToolkit/Inputs/TextInput2/TextInput2';
import './BulkImportPreviewModal.css';
import '../../../TapyblAI/GenerateAIAssistant/view/styles.css';

interface Course {
  id: string;
  title: string;
  avatarUrl?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  learners: ParsedLearner[];
  errors: string[];
  unmappedColumns: string[];
  onConfirm: (
    selectedLearners: ParsedLearner[],
    selectedCourseIds: string[],
    dueDate?: Date,
  ) => void;
  courses: Course[];
  isLoading: boolean;
}

const BulkImportPreviewModal = React.memo(
  ({
    isOpen,
    onClose,
    learners,
    errors,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    unmappedColumns,
    onConfirm,
    courses,
    isLoading,
  }: Props) => {
    const [localLearners, setLocalLearners] =
      useState<ParsedLearner[]>(learners);
    const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
    const [courseSearch, setCourseSearch] = useState('');
    const [dueDate, setDueDate] = useState<Date | null>(null);

    // Reset state when modal opens with new data
    React.useEffect(() => {
      setLocalLearners(learners);
      setSelectedCourseIds([]);
      setCourseSearch('');
      setDueDate(null);
    }, [learners]);

    const duplicateGroups = useMemo(() => {
      const groups: Record<string, ParsedLearner[]> = {};
      localLearners.forEach(learner => {
        if (learner.isDuplicate && learner.duplicateGroup) {
          if (!groups[learner.duplicateGroup]) {
            groups[learner.duplicateGroup] = [];
          }
          groups[learner.duplicateGroup].push(learner);
        }
      });
      return groups;
    }, [localLearners]);

    const hasDuplicates = Object.keys(duplicateGroups).length > 0;
    const hasErrors = localLearners.some(l => l.hasError);

    // Selectable learners are those without errors and not email duplicates
    const selectableLearners = localLearners.filter(
      l => !l.hasError && !l.isEmailDuplicate,
    );
    const selectedValidCount = localLearners.filter(
      l => l.isSelected && !l.hasError && !l.isEmailDuplicate,
    ).length;
    const allSelected =
      selectableLearners.length > 0 &&
      selectableLearners.every(l => l.isSelected);

    const handleToggleLearner = (id: string) => {
      setLocalLearners(prev =>
        prev.map(l => (l.id === id ? {...l, isSelected: !l.isSelected} : l)),
      );
    };

    const handleToggleAll = () => {
      const newValue = !allSelected;
      // Only toggle selectable learners (not errors, not email duplicates)
      setLocalLearners(prev =>
        prev.map(l =>
          l.hasError || l.isEmailDuplicate ? l : {...l, isSelected: newValue},
        ),
      );
    };

    const courseOptions = useMemo(() => {
      const filtered = courseSearch
        ? courses.filter(c =>
            c.title.toLowerCase().includes(courseSearch.toLowerCase()),
          )
        : courses;
      return filtered.map(course => ({
        key: course.id,
        value: course.title,
        thumb: course.avatarUrl,
      }));
    }, [courses, courseSearch]);

    const handleConfirm = () => {
      const selected = localLearners.filter(l => l.isSelected);
      onConfirm(
        selected,
        selectedCourseIds,
        selectedCourseIds.length > 0 && dueDate instanceof Date
          ? dueDate
          : undefined,
      );
    };

    if (!isOpen) return null;

    return (
      <ModalDialog
        onClose={onClose}
        isLoading={isLoading}
        loadingText={learnersText.pleaseWaitProcessing}>
        <div className="BulkImportPreviewModal">
          <h2 className="ModalTitle">{learnersText.importPreview}</h2>

          {errors.length > 0 && (
            <div className="ErrorsSection">
              <p className="ErrorsTitle">{learnersText.parsingIssues}</p>
              <ul className="ErrorsList">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {hasErrors && (
            <div className="ErrorWarning">
              <span className="icon-alert-circle ErrorIcon" />
              <p>{learnersText.rowsHaveErrors}</p>
            </div>
          )}

          {hasDuplicates && (
            <div className="DuplicateWarning">
              <span className="icon-alert-triangle WarningIcon" />
              <p>{learnersText.duplicateEntriesFound}</p>
            </div>
          )}

          <div className="LearnersSection">
            <div className="LearnersHeader">
              <Checkbox
                checked={allSelected}
                onCheck={handleToggleAll}
                text={`${learnersText.selectAll} (${selectedValidCount}/${selectableLearners.length})`}
                className="SelectAllCheckbox"
              />
            </div>

            <div className="LearnersTableContainer">
              <table className="LearnersTable">
                <thead>
                  <tr>
                    <th></th>
                    <th>First name</th>
                    <th>Last name</th>
                    <th>Email</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {localLearners.map(learner => {
                    let rowClass = '';
                    if (learner.hasError) {
                      rowClass = 'ErrorRow';
                    } else if (learner.isEmailDuplicate) {
                      rowClass = 'EmailDuplicateRow';
                    } else if (learner.isDuplicate) {
                      rowClass = 'DuplicateRow';
                    }
                    const canSelect =
                      !learner.hasError && !learner.isEmailDuplicate;
                    return (
                      <tr key={learner.id} className={rowClass}>
                        <td>
                          {canSelect ? (
                            <Checkbox
                              checked={learner.isSelected}
                              onCheck={() => handleToggleLearner(learner.id)}
                              text=""
                            />
                          ) : (
                            <div className="DisabledCheckboxPlaceholder" />
                          )}
                        </td>
                        <td>{learner.firstName || '-'}</td>
                        <td>{learner.lastName || '-'}</td>
                        <td>{learner.email || '-'}</td>
                        <td className="StatusCell">
                          {learner.hasError ? (
                            <span className="ErrorBadge">
                              {learner.errorMessage}
                            </span>
                          ) : learner.isEmailDuplicate ? (
                            <span className="ErrorBadge">
                              {learnersText.duplicateEmail}
                            </span>
                          ) : learner.isDuplicate ? (
                            <span className="DuplicateBadge">
                              {learnersText.possibleDuplicate}
                            </span>
                          ) : (
                            <span className="OkBadge">OK</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="CourseEnrollSection">
            <p className="CourseEnrollTitle">
              {learnersText.autoEnrollToCourses}{' '}
              <span className="OptionalLabel">{learnersText.optional}</span>
            </p>
            <SearchDropdown
              options={courseOptions}
              selectedOptions={selectedCourseIds}
              setSelectedOptions={setSelectedCourseIds}
              placeholder={learnersText.selectCourses}
              fieldError={false}
              className="SearchDropdown_Container"
              onSearch={setCourseSearch}
              search={courseSearch}
            />
          </div>

          {selectedCourseIds.length > 0 && (
            <div className="DueDateSection">
              <p className="CourseEnrollTitle">
                {learnersText.dueDate}{' '}
                <span className="OptionalLabel">{learnersText.optional}</span>
              </p>
              <div className="DueDateInputRow">
                <TextInput2
                  uiType="minimal"
                  label=""
                  placeholder={learnersText.dueDatePlaceholder}
                  value={dueDate ? dueDate.toDateString() : ''}
                  disabled={true}
                  onChange={() => {
                    // do nothing
                  }}
                />
                {dueDate && (
                  <Button
                    title={learnersText.clearDueDate}
                    uiType="text"
                    onClick={() => setDueDate(null)}
                    className="ClearDueDateButton"
                  />
                )}
              </div>
              <DatePickerComponent dueDate={dueDate} setDueDate={setDueDate} />
            </div>
          )}

          <div className="ModalFooter">
            <Button
              onClick={onClose}
              title={learnersText.CancelButton}
              uiType="text"
              className="CancelButton"
            />
            <Button
              onClick={handleConfirm}
              title={`${learnersText.importLearners} ${selectedValidCount} ${
                selectedValidCount !== 1
                  ? learnersText.learners
                  : learnersText.learner
              }`}
              uiType="action"
              disabled={selectedValidCount === 0 || isLoading}
              isLoading={isLoading}
            />
          </div>
        </div>
      </ModalDialog>
    );
  },
);

export default BulkImportPreviewModal;
